import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, Wrench, AlertTriangle, Clock, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function MechanicWorksheet({ open, onOpenChange, mechanic: initialMechanic, onClose }) {
  const [loading, setLoading] = useState(false);
  const [worksheet, setWorksheet] = useState(null);
  const [mechanics, setMechanics] = useState([]);
  const [selectedMechanic, setSelectedMechanic] = useState(initialMechanic || "");
  const printRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchMechanics();
      if (initialMechanic) {
        setSelectedMechanic(initialMechanic);
        fetchWorksheet(initialMechanic);
      } else {
        fetchWorksheet(null);
      }
    }
  }, [open, initialMechanic]);

  const fetchMechanics = async () => {
    try {
      const response = await api.get("/mechanics/summary");
      setMechanics(response.data.mechanics || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchWorksheet = async (mechanic) => {
    setLoading(true);
    try {
      const url = mechanic ? `/mechanics/worksheet?mechanic=${encodeURIComponent(mechanic)}` : "/mechanics/worksheet";
      const response = await api.get(url);
      setWorksheet(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMechanicChange = (value) => {
    setSelectedMechanic(value);
    fetchWorksheet(value === "all" ? null : value);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Fiche Mecanicien - ${worksheet?.mechanic || "Tous"}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { font-size: 18px; margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 5px; }
          .header-info { margin-bottom: 15px; color: #666; }
          .scooter-card { border: 1px solid #ccc; margin-bottom: 15px; page-break-inside: avoid; }
          .scooter-header { background: #f0f0f0; padding: 8px; border-bottom: 1px solid #ccc; }
          .scooter-header h2 { font-size: 14px; margin: 0; }
          .priority-1 { border-left: 4px solid #ef4444; }
          .priority-2 { border-left: 4px solid #eab308; }
          .priority-3 { border-left: 4px solid #3b82f6; }
          .scooter-info { padding: 8px; display: flex; gap: 20px; flex-wrap: wrap; }
          .info-item { }
          .info-label { color: #666; font-size: 10px; }
          .info-value { font-weight: bold; }
          .tasks-table { width: 100%; border-collapse: collapse; }
          .tasks-table th, .tasks-table td { border: 1px solid #ccc; padding: 6px; text-align: left; }
          .tasks-table th { background: #f5f5f5; font-size: 11px; }
          .checkbox { width: 20px; height: 20px; border: 2px solid #000; display: inline-block; margin-right: 5px; }
          .priority-badge { padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold; }
          .urgent { background: #fee2e2; color: #dc2626; }
          .normal { background: #fef3c7; color: #d97706; }
          .can-wait { background: #dbeafe; color: #2563eb; }
          .footer { margin-top: 20px; border-top: 1px solid #ccc; padding-top: 10px; }
          .signature-line { margin-top: 30px; border-top: 1px solid #000; width: 200px; }
          @media print {
            body { padding: 10px; }
            .scooter-card { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>FICHE DE TRAVAIL MECANICIEN</h1>
        <div class="header-info">
          <strong>Mecanicien:</strong> ${worksheet?.mechanic || "Tous les mecaniciens"}<br>
          <strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}<br>
          <strong>Total:</strong> ${worksheet?.total_scooters || 0} scooters, ${worksheet?.total_tasks || 0} taches
        </div>
        
        ${worksheet?.items?.map(item => `
          <div class="scooter-card priority-${item.repair_priority}">
            <div class="scooter-header">
              <h2>
                <span class="priority-badge ${item.repair_priority === 1 ? 'urgent' : item.repair_priority === 2 ? 'normal' : 'can-wait'}">
                  ${item.repair_priority === 1 ? 'URGENT' : item.repair_priority === 2 ? 'NORMAL' : 'PEUT ATTENDRE'}
                </span>
                ${item.scooter_code || item.scooter_id} - ${item.brand} ${item.model} ${item.year}
              </h2>
            </div>
            <div class="scooter-info">
              <div class="info-item">
                <div class="info-label">ID Scooter</div>
                <div class="info-value">${item.scooter_id}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Couleur</div>
                <div class="info-value">${item.color || '-'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Etat Reception</div>
                <div class="info-value">${'*'.repeat(item.reception_condition)}${'_'.repeat(5 - item.reception_condition)}/5</div>
              </div>
            </div>
            <table class="tasks-table">
              <thead>
                <tr>
                  <th style="width:30px">OK</th>
                  <th>Tache</th>
                  <th style="width:80px">Cout Est.</th>
                  <th style="width:80px">Cout Reel</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                ${item.tasks.map(task => `
                  <tr>
                    <td><span class="checkbox"></span></td>
                    <td><strong>${task.task_name}</strong>${task.description ? `<br><small>${task.description}</small>` : ''}</td>
                    <td>${task.estimated_cost} EUR</td>
                    <td>_______ EUR</td>
                    <td>${task.notes || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `).join('') || '<p>Aucune tache a afficher</p>'}
        
        <div class="footer">
          <p>Signature du mecanicien: <span class="signature-line"></span></p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getPriorityLabel = (p) => {
    switch (p) {
      case 1: return { label: "Urgent", class: "bg-red-500 text-white" };
      case 2: return { label: "Normal", class: "bg-yellow-500 text-black" };
      case 3: return { label: "Peut attendre", class: "bg-blue-500 text-white" };
      default: return { label: "-", class: "" };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-primary" />
            Fiche de Travail Mecanicien
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <Select value={selectedMechanic || "all"} onValueChange={handleMechanicChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selectionner un mecanicien" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les mecaniciens</SelectItem>
                {mechanics.map((m) => (
                  <SelectItem key={m.name} value={m.name}>
                    {m.name} ({m.pending + m.in_progress} taches)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handlePrint} disabled={loading || !worksheet?.items?.length}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimer
          </Button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground">
            Chargement...
          </div>
        ) : worksheet?.items?.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            <CheckSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune tache en attente</p>
          </div>
        ) : (
          <div ref={printRef} className="space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground border-b pb-2">
              <span>Mecanicien: <strong>{worksheet?.mechanic}</strong></span>
              <span>{worksheet?.total_scooters} scooters, {worksheet?.total_tasks} taches</span>
            </div>

            {worksheet?.items?.map((item) => {
              const priority = getPriorityLabel(item.repair_priority);
              return (
                <div
                  key={item.scooter_id}
                  className={`border rounded-lg overflow-hidden ${
                    item.repair_priority === 1
                      ? "border-red-500"
                      : item.repair_priority === 2
                      ? "border-yellow-500"
                      : "border-blue-500"
                  }`}
                >
                  <div className="bg-secondary/50 p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={priority.class}>{priority.label}</Badge>
                      <span className="font-bold">
                        {item.scooter_code || item.scooter_id}
                      </span>
                      <span className="text-muted-foreground">
                        {item.brand} {item.model} {item.year}
                      </span>
                    </div>
                    <Badge variant="outline">{item.task_count} taches</Badge>
                  </div>

                  <div className="p-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Tache</th>
                          <th className="text-left py-2 w-24">Cout Est.</th>
                          <th className="text-left py-2 w-24">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.tasks.map((task) => (
                          <tr key={task.task_id} className="border-b last:border-0">
                            <td className="py-2">
                              <div className="font-medium">{task.task_name}</div>
                              {task.description && (
                                <div className="text-xs text-muted-foreground">{task.description}</div>
                              )}
                            </td>
                            <td className="py-2 font-mono">{task.estimated_cost} EUR</td>
                            <td className="py-2">
                              <Badge
                                variant={task.status === "in_progress" ? "default" : "outline"}
                                className={task.status === "in_progress" ? "bg-orange-500" : ""}
                              >
                                {task.status === "pending" ? "A faire" : "En cours"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
